import { useEffect, useMemo, useState } from "react";
import { Calculator, MousePointerClick, Scale } from "lucide-react";
import { fetchPrograms } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Field = ({ label, value, onChange, prefix, suffix, testId, step = 1 }) => (
  <div>
    <label className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 block mb-2">{label}</label>
    <div className="relative">
      {prefix && <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{prefix}</span>}
      <input
        type="number" min="0" step={step} value={value} data-testid={testId}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className={`w-full ${prefix ? "pl-9" : "pl-4"} pr-12 py-3 rounded-lg bg-white border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500`}
      />
      {suffix && <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{suffix}</span>}
    </div>
  </div>
);

const Result = ({ label, value, testId, big }) => (
  <div className={`rounded-lg p-6 ${big ? "bg-slate-900 text-white" : "bg-white border border-slate-200"}`}>
    <p className={`text-[10px] uppercase tracking-[0.2em] font-semibold mb-1 ${big ? "text-emerald-400" : "text-slate-400"}`}>{label}</p>
    <p data-testid={testId} className={`font-heading font-medium ${big ? "text-3xl" : "text-2xl"}`}>{value}</p>
  </div>
);

const money = (v) => `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function CalculatorsPage() {
  const [programs, setPrograms] = useState([]);

  // Commission calc
  const [traffic, setTraffic] = useState(10000);
  const [ctr, setCtr] = useState(3);
  const [convRate, setConvRate] = useState(2);
  const [aov, setAov] = useState(100);
  const [commissionPct, setCommissionPct] = useState(20);

  // EPC calc
  const [epcEarnings, setEpcEarnings] = useState(500);
  const [epcClicks, setEpcClicks] = useState(250);

  // ROI
  const [roiProgramA, setRoiProgramA] = useState("");
  const [roiProgramB, setRoiProgramB] = useState("");
  const [roiClicks, setRoiClicks] = useState(1000);

  useEffect(() => {
    fetchPrograms({ limit: 60, sort: "popularity" }).then((d) => setPrograms(d.programs)).catch(console.error);
  }, []);

  const clicks = traffic * (ctr / 100);
  const sales = clicks * (convRate / 100);
  const monthlyEarnings = sales * aov * (commissionPct / 100);
  const epc = epcClicks > 0 ? epcEarnings / epcClicks : 0;

  const progA = useMemo(() => programs.find((p) => p.slug === roiProgramA), [programs, roiProgramA]);
  const progB = useMemo(() => programs.find((p) => p.slug === roiProgramB), [programs, roiProgramB]);

  const applyProgram = (slug) => {
    const p = programs.find((x) => x.slug === slug);
    if (!p) return;
    setConvRate(p.conversion_rate);
    if (p.aov) setAov(p.aov);
    if (p.commission_type === "percentage" || p.commission_type === "recurring") setCommissionPct(p.commission_value);
  };

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-8 py-12">
      <p className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 mb-2">Tools</p>
      <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-4">Calculators & estimators</h1>
      <p className="text-slate-600 max-w-2xl mb-12 leading-relaxed">Model your potential earnings with real program benchmarks before you commit.</p>

      <Tabs defaultValue="commission">
        <TabsList className="rounded-full h-auto p-1.5 mb-10 flex-wrap">
          <TabsTrigger value="commission" data-testid="tab-commission" className="rounded-full px-5 py-2 gap-2"><Calculator className="w-4 h-4" /> Commission</TabsTrigger>
          <TabsTrigger value="epc" data-testid="tab-epc" className="rounded-full px-5 py-2 gap-2"><MousePointerClick className="w-4 h-4" /> EPC</TabsTrigger>
          <TabsTrigger value="roi" data-testid="tab-roi" className="rounded-full px-5 py-2 gap-2"><Scale className="w-4 h-4" /> ROI Comparison</TabsTrigger>
        </TabsList>

        <TabsContent value="commission">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            <div className="lg:col-span-3 space-y-6">
              <div>
                <label className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 block mb-2">Preload from a program (optional)</label>
                <Select onValueChange={applyProgram}>
                  <SelectTrigger data-testid="calc-program-select" className="rounded-lg bg-white"><SelectValue placeholder="Choose a program…" /></SelectTrigger>
                  <SelectContent>
                    {programs.map((p) => <SelectItem key={p.slug} value={p.slug}>{p.name} — {p.commission}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label="Monthly visitors" value={traffic} onChange={setTraffic} testId="calc-traffic-input" />
                <Field label="Click-through rate" value={ctr} onChange={setCtr} suffix="%" step={0.1} testId="calc-ctr-input" />
                <Field label="Conversion rate" value={convRate} onChange={setConvRate} suffix="%" step={0.1} testId="calc-conversion-input" />
                <Field label="Average order value" value={aov} onChange={setAov} prefix="$" testId="calc-aov-input" />
                <Field label="Commission rate" value={commissionPct} onChange={setCommissionPct} suffix="%" step={0.5} testId="calc-commission-input" />
              </div>
            </div>
            <div className="lg:col-span-2 space-y-4">
              <Result label="Estimated clicks / mo" value={Math.round(clicks).toLocaleString()} testId="calc-result-clicks" />
              <Result label="Estimated sales / mo" value={sales.toFixed(1)} testId="calc-result-sales" />
              <Result label="Estimated monthly earnings" value={money(monthlyEarnings)} testId="calc-result-earnings" big />
              <Result label="Estimated yearly earnings" value={money(monthlyEarnings * 12)} testId="calc-result-yearly" />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="epc">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            <div className="lg:col-span-3 space-y-6">
              <Field label="Total commissions earned" value={epcEarnings} onChange={setEpcEarnings} prefix="$" testId="epc-earnings-input" />
              <Field label="Total clicks sent" value={epcClicks} onChange={setEpcClicks} testId="epc-clicks-input" />
              <p className="text-sm text-slate-500 leading-relaxed">EPC = Total Earnings ÷ Total Clicks. Use it to compare offers regardless of commission structure — the higher the EPC, the more each visitor is worth.</p>
            </div>
            <div className="lg:col-span-2 space-y-4">
              <Result label="Your EPC" value={money(epc)} testId="epc-result" big />
              <Result label="Value of 1,000 clicks" value={money(epc * 1000)} testId="epc-result-1000" />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="roi">
          <div className="space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div>
                <label className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 block mb-2">Program A</label>
                <Select value={roiProgramA} onValueChange={setRoiProgramA}>
                  <SelectTrigger data-testid="roi-program-a-select" className="rounded-lg bg-white"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>{programs.map((p) => <SelectItem key={p.slug} value={p.slug}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 block mb-2">Program B</label>
                <Select value={roiProgramB} onValueChange={setRoiProgramB}>
                  <SelectTrigger data-testid="roi-program-b-select" className="rounded-lg bg-white"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>{programs.map((p) => <SelectItem key={p.slug} value={p.slug}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Field label="Monthly clicks you can send" value={roiClicks} onChange={setRoiClicks} testId="roi-clicks-input" />
            </div>
            {progA && progB && (
              <div data-testid="roi-results" className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[progA, progB].map((p) => {
                  const est = p.epc * roiClicks;
                  const winner = est >= Math.max(progA.epc, progB.epc) * roiClicks;
                  return (
                    <div key={p.slug} className={`rounded-lg p-8 border ${winner ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200"}`}>
                      <p className="font-heading text-xl font-semibold mb-1">{p.name}</p>
                      <p className="text-xs text-slate-500 mb-5">{p.commission} · ${p.epc.toFixed(2)} EPC</p>
                      <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">Est. monthly return</p>
                      <p className={`font-heading text-3xl font-medium ${winner ? "text-emerald-700" : ""}`}>{money(est)}</p>
                      {winner && <p className="mt-3 inline-block text-xs font-semibold text-emerald-700 bg-emerald-100 px-3 py-1 rounded-full">Better fit for your traffic</p>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </main>
  );
}
